import { parseEvalString } from "@/shared";
import type { EntityViewType, StaticEntityType } from "@config";
import { useViewContext } from "@editor-providers";
import classNames from "classnames";
import type { ReactNode } from "react";
import "../styles/index.scss";
import type {
  EditorDataProps,
  EditorSlotsProps,
  SlideSettingsType,
} from "../types";
import { Background } from "./Background";
import { SlideStyled, SlideWrapper } from "./Slide.styled";
import Watermark from "./Watermark";

export const SlideView = ({
  entity,
  children,
  editorData,
  editorSlots,
}: {
  children: ReactNode;
  entity: EntityViewType;
  editorData?: EditorDataProps;
  editorSlots?: EditorSlotsProps;
}) => {
  const { view } = useViewContext();
  const isEditor = view === "editor";
  const isThumbnail = view === "thumbnail";

  const { settings, id } = entity as StaticEntityType<SlideSettingsType>;

  if (!isEditor && !isThumbnail) {
    if (settings.isDynamicallyHidden) {
      const isHidden = parseEvalString(settings.isDynamicallyHidden);
      if (isHidden) return null;
    }
    if (settings.isHidden) return null;
  }

  return (
    <SlideStyled
      id={`slide-id-${id}`}
      style={
        {
          "--slide-background-color": settings.backgroundColor || "#ffffff",
        } as React.CSSProperties
      }
      className={classNames(
        "slide slide-wrapper-dELwSneFkh after:absolute max-md:tw-px-slide-mobile-padding-x max-md:tw-py-slide-mobile-padding-y tw-relative tw-flex tw-min-h-[250px] tw-w-full tw-flex-col tw-px-slide-padding-x tw-py-slide-padding-y",
        {
          fullheight: settings.minHeight === "screenFit",
          "--active": editorData?.isActive,
          "--collapsed": editorData?.collapsed,
          "--active-thread": editorData?.isActiveThread,
          "--mobile-hidden": settings.deviceVisibility === "showOnDesktop",
          "--desktop-hidden": settings.deviceVisibility === "showOnMobile",
          "--is-editor": isEditor,
          "--is-thumbnail": view === "thumbnail",
        },
      )}
      {...DATA_ATTRIBUTES({ settings })}
      {...STYLED_ATTRIBUTES({ settings })}
      {...EDITOR_ATTRIBUTES({
        id,
        isEditor,
        active: editorData?.isActive,
      })}
    >
      <Watermark />
      {editorSlots?.cursorsSlot}
      <Background settings={settings} />
      <SlideWrapper
        $minHeight={settings.minHeight}
        $editor={isEditor}
        className="inner-wrapper tw-relative tw-z-1 tw-flex tw-w-full tw-max-w-full tw-flex-col [align-items:inherit] [justify-content:inherit]"
      >
        {children}
      </SlideWrapper>
      {editorSlots?.entityThreadLabel}
      {editorSlots?.coeditingEntityOutline}
      {editorSlots?.slideActions}
    </SlideStyled>
  );
};

//  ======= ATTRIBUTE CREATION =======
const STYLED_ATTRIBUTES = ({ settings }: { settings: SlideSettingsType }) => ({
  $verticalAlignment: settings.verticalAlignment,
  $horizontalAlignment: settings.horizontalAlignment,
  $minHeight: settings.minHeight,
});

const DATA_ATTRIBUTES = ({ settings }: { settings: SlideSettingsType }) => {
  const { minHeight, isAnchorActive, anchorName } = settings;
  return {
    "data-slide-min-height": minHeight === "screenFit" ? "100vh" : minHeight,
    "data-anchor-target": isAnchorActive ? anchorName : "",
    "data-slide-is-pdf-slide": !!settings.isPdfSlide,
  };
};
const EDITOR_ATTRIBUTES = ({
  id,
  isEditor = false,
  active,
}: {
  id: string;
  isEditor: boolean;
  active?: boolean;
}) => {
  const payload: Record<string, string | number> = {
    "data-layer-id": id,
    "data-layer-type": "slide",
  };
  if (active && isEditor) payload["tabIndex"] = 0;
  return payload;
};

//  ======= ATTRIBUTE CREATION =======
